import { useState, useEffect, useCallback, useRef } from 'react';
import { messagingService } from '../services/messagingService';
import api from '../../../utils/api';
import safeStorage from '../../../utils/safeStorage';
import {
  decodeAccessTokenUserId,
  ensureMessagingIdentity,
  getLocalMessagingSecretKey,
} from '../../../utils/messagingIdentity';
import { enrichMessageWithDecryptedBody } from '../../../utils/messageE2ee';
import { sendEncryptedUserMessage } from '../../../utils/messageSendE2ee';
import {
  bindMessageThreadCacheUser,
  clearInFlightThreadLoad,
  getCachedThread,
  getInFlightThreadLoad,
  setCachedThread,
  setInFlightThreadLoad,
} from '../utils/messageThreadCache';
import { scrollToLatestMessage, THREAD_SCROLL_LIVE } from '../utils/messageThreadScroll';

export const useMessaging = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [typingUsers] = useState(new Set());
  const [unreadCounts, setUnreadCounts] = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  const [hasNewMessages, setHasNewMessages] = useState(false);
  const wsRef = useRef(null);
  const atBottomRef = useRef(true);
  
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Get current user from JWT token (base64url-safe decode for user_id)
  const getCurrentUser = useCallback(() => {
    const token = safeStorage.getItem('token');
    if (!token) return null;
    const id = decodeAccessTokenUserId(token);
    if (id == null) return null;
    try {
      const parts = token.split('.');
      const payloadB64 = parts[1];
      if (!payloadB64) return { id, username: 'Unknown' };
      const padded = payloadB64.replace(/-/g, '+').replace(/_/g, '/');
      const pad = padded.length % 4;
      const base64 = pad ? padded + '='.repeat(4 - pad) : padded;
      const payload = JSON.parse(atob(base64));
      return { id, username: payload.username || 'Unknown' };
    } catch (error) {
      console.error('Error parsing JWT token:', error);
      return { id, username: 'Unknown' };
    }
  }, []);

  const invalidateMessagingCacheIfUserChanged = useCallback(() => {
    const token = safeStorage.getItem('token');
    const uid = token ? decodeAccessTokenUserId(token) : null;
    if (!bindMessageThreadCacheUser(uid)) return;
    setMessages([]);
    setSelectedConversation(null);
    setConversations([]);
    setUnreadCounts({});
  }, []);

  // Load conversations
  const loadConversations = useCallback(async () => {
    try {
      invalidateMessagingCacheIfUserChanged();
      setLoading(true);
      setError(null);
      const data = await messagingService.getConversations();
      setConversations(data);
      
      // Calculate unread counts
      const counts = {};
      data.forEach(conv => {
        counts[conv.id] = conv.unread_count || 0;
      });
      setUnreadCounts(counts);
    } catch (err) {
      console.error('Failed to load conversations:', err);
      setError('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, [invalidateMessagingCacheIfUserChanged]);

  const mergeMessages = (prev, incoming) => {
    const byId = new Map((prev || []).map(m => [m.id, m]));
    for (const m of incoming || []) byId.set(m.id, m);
    return Array.from(byId.values()).sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  };

  const decryptMessages = useCallback((arr) => {
    const sk = getLocalMessagingSecretKey();
    if (!sk || !Array.isArray(arr)) return arr;
    return arr.map((m) => enrichMessageWithDecryptedBody(m, sk));
  }, []);

  const applyReactionUpdate = useCallback((conversationId, payload) => {
    if (!conversationId || !payload?.message_id) return;
    const cached = getCachedThread(conversationId);
    if (!cached || !Array.isArray(cached.messages)) return;
    const updated = cached.messages.map(m => {
      if (m.id !== payload.message_id) return m;
      return { ...m, reactions: Array.isArray(payload.reactions) ? payload.reactions : m.reactions };
    });
    setCachedThread(conversationId, { ...cached, messages: updated });
    if (selectedConversation && selectedConversation.id === conversationId) {
      setMessages(updated);
    }
  }, [selectedConversation]);

  const appendLiveMessage = useCallback((conversationId, msg) => {
    if (!conversationId || !msg) return;
    const cached = getCachedThread(conversationId) || { messages: [], oldestId: null, newestId: null, hasMoreBefore: true };
    const merged = mergeMessages(cached.messages, [msg]);
    setCachedThread(conversationId, {
      messages: merged,
      oldestId: cached.oldestId || (merged[0] && merged[0].id) || null,
      newestId: merged[merged.length - 1] && merged[merged.length - 1].id,
      hasMoreBefore: cached.hasMoreBefore,
    });
    setConversations(prev =>
      prev.map(c =>
        c.id === conversationId ? { ...c, last_message: msg } : c
      )
    );
    if (selectedConversation && selectedConversation.id === conversationId) {
      setMessages(merged);
      if (atBottomRef.current) {
        setTimeout(() => { scrollToLatestMessage(messagesEndRef.current, THREAD_SCROLL_LIVE); }, 20);
      } else {
        setHasNewMessages(true);
      }
    }
  }, [selectedConversation]);

  const loadMessages = useCallback(async (conversationId) => {
    if (!conversationId) return;
    invalidateMessagingCacheIfUserChanged();
    const existing = getInFlightThreadLoad(conversationId);
    if (existing) return existing;
    const run = (async () => {
    try {
      setError(null);
      const cached = getCachedThread(conversationId);
      if (cached && Array.isArray(cached.messages)) {
        setMessages(decryptMessages(cached.messages));
      }
      // Delta fetch after newestId
      const params = cached && cached.newestId ? { after: cached.newestId, limit: 50 } : { limit: 30 };
      const resp = await messagingService.getMessages(conversationId, params);
      const incoming = Array.isArray(resp.results) ? resp.results : (Array.isArray(resp) ? resp : []);
      const merged = decryptMessages(mergeMessages(cached?.messages || [], incoming));
      const nextCache = {
        messages: merged,
        oldestId: resp.oldest_id || (merged[0] && merged[0].id) || null,
        newestId: resp.newest_id || (merged[merged.length - 1] && merged[merged.length - 1].id) || null,
        hasMoreBefore: resp.has_more_before ?? (cached?.hasMoreBefore ?? false),
      };
      setCachedThread(conversationId, nextCache);
      setMessages(merged);
      // Update conversation preview with the latest fully loaded message
      const latest = merged[merged.length - 1] || null;
      if (latest) {
        setConversations(prev =>
          prev.map(c =>
            c.id === conversationId ? { ...c, last_message: latest } : c
          )
        );
      }

      // Mark read (debounced would be better; keeping simple)
      try { await messagingService.markConversationAsRead(conversationId); } catch (_) {}
      setUnreadCounts(prev => ({ ...prev, [conversationId]: 0 }));
      setConversations(prev => prev.map(c => c.id === conversationId ? { ...c, unread_count: 0 } : c));
    } catch (err) {
      console.error('Failed to load messages:', err);
      setError('Failed to load messages');
    } finally {
      clearInFlightThreadLoad(conversationId);
    }
    })();
    setInFlightThreadLoad(conversationId, run);
    return run;
  }, [setConversations, setUnreadCounts, decryptMessages, invalidateMessagingCacheIfUserChanged]);

  const loadOlder = useCallback(async (conversationId) => {
    if (!conversationId) return;
    invalidateMessagingCacheIfUserChanged();
    const cached = getCachedThread(conversationId);
    if (!cached || !cached.hasMoreBefore || !cached.oldestId) return;
    try {
      setLoadingOlder(true);
      const resp = await messagingService.getMessages(conversationId, { before: cached.oldestId, limit: 50 });
      const incoming = Array.isArray(resp.results) ? resp.results : [];
      if (incoming.length === 0) {
        setCachedThread(conversationId, { ...cached, hasMoreBefore: false });
        return;
      }
      const merged = decryptMessages(mergeMessages(cached.messages, incoming));
      setCachedThread(conversationId, {
        messages: merged,
        oldestId: resp.oldest_id || (merged[0] && merged[0].id) || cached.oldestId,
        newestId: cached.newestId,
        hasMoreBefore: resp.has_more_before ?? false,
      });
      setMessages(merged);
    } catch (e) {
      // non-fatal
    } finally {
      setLoadingOlder(false);
    }
  }, [decryptMessages, invalidateMessagingCacheIfUserChanged]);

  const hasMoreBefore = useCallback((conversationId) => {
    if (!conversationId) return false;
    const cached = getCachedThread(conversationId);
    return !!(cached && cached.hasMoreBefore);
  }, []);

  // Send a message
  const sendMessage = useCallback(async (content, conversationId = null) => {
    if (!content.trim()) return null;
    
    const targetConversationId = conversationId || selectedConversation?.id;
    if (!targetConversationId) return null;

    try {
      setSending(true);
      setError(null);

      let conv =
        selectedConversation?.id === targetConversationId ? selectedConversation : null;
      if (!conv?.participants?.length) {
        conv = await messagingService.getConversation(targetConversationId);
      }

      const newMessageRaw = await sendEncryptedUserMessage(
        messagingService,
        conv,
        content.trim(),
        'text'
      );
      const sk = getLocalMessagingSecretKey();
      const newMessage = sk
        ? enrichMessageWithDecryptedBody(newMessageRaw, sk)
        : newMessageRaw;

      setMessages((prev) => {
        const next = [...prev, newMessage];
        const cached = getCachedThread(targetConversationId) || {};
        setCachedThread(targetConversationId, {
          messages: next,
          oldestId: cached.oldestId || next[0]?.id || null,
          newestId: newMessage.id,
          hasMoreBefore: cached.hasMoreBefore ?? false,
        });
        return next;
      });
      
      // Update conversation's last message
      setConversations(prev => prev.map(conv => 
        conv.id === targetConversationId 
          ? { ...conv, last_message: newMessage }
          : conv
      ));
      
      setTimeout(() => {
        scrollToLatestMessage(messagesEndRef.current, THREAD_SCROLL_LIVE);
      }, 100);
      
      return newMessage;
    } catch (err) {
      console.error('Failed to send message:', err);
      setError('Failed to send message');
      return null;
    } finally {
      setSending(false);
    }
  }, [selectedConversation]);

  // Create a new conversation
  const createConversation = useCallback(async (conversationData) => {
    try {
      setError(null);
      const newConversation = await messagingService.createConversation(conversationData);
      setConversations(prev => [newConversation, ...prev]);
      return newConversation;
    } catch (err) {
      console.error('Failed to create conversation:', err);
      // Provide friendly errors for common validation cases
      const apiMsg = err?.response?.data || err?.message;
      const text = typeof apiMsg === 'string' ? apiMsg : (apiMsg?.detail || apiMsg?.error || JSON.stringify(apiMsg));
      if (text && String(text).toLowerCase().includes('direct messages are only allowed')) {
        setError('You can only message hunting buddies. Send a buddy request first.');
      } else if (text && String(text).toLowerCase().includes('must have exactly one participant')) {
        setError('Direct messages must have exactly one recipient.');
      } else {
        setError('Could not create conversation. Please try again.');
      }
      return null;
    }
  }, []);

  // Find existing direct message conversation
  const findDirectMessage = useCallback(async (userId) => {
    try {
      setError(null);
      const result = await messagingService.findDirectMessage(userId);
      return result;
    } catch (err) {
      console.error('Failed to find direct message:', err);
      setError('Failed to find direct message');
      return null;
    }
  }, []);

  // Send typing indicator
  const sendTypingIndicator = useCallback(async (isTyping, conversationId = null) => {
    const targetConversationId = conversationId || selectedConversation?.id;
    if (!targetConversationId) return;

    try {
      await messagingService.sendTypingIndicator(targetConversationId, isTyping);
    } catch (err) {
      console.error('Failed to send typing indicator:', err);
    }
  }, [selectedConversation]);

  // Handle typing with debounce
  const handleTyping = useCallback((isTyping, conversationId = null) => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    sendTypingIndicator(isTyping, conversationId);
    
    if (isTyping) {
      typingTimeoutRef.current = setTimeout(() => {
        sendTypingIndicator(false, conversationId);
      }, 3000);
    }
  }, [sendTypingIndicator]);

  // Search messages
  const searchMessages = useCallback(async (query, conversationId = null) => {
    try {
      setError(null);
      const results = await messagingService.searchMessages(query, conversationId);
      return results;
    } catch (err) {
      console.error('Failed to search messages:', err);
      setError('Failed to search messages');
      return [];
    }
  }, []);

  // Delete a message
  const deleteMessage = useCallback(async (messageId) => {
    try {
      setError(null);
      await messagingService.deleteMessage(messageId);
      setMessages(prev => prev.filter(msg => msg.id !== messageId));
    } catch (err) {
      console.error('Failed to delete message:', err);
      setError('Failed to delete message');
    }
  }, []);

  // Delete a conversation (actually hide it from user's view)
  const deleteConversation = useCallback(async (conversationId) => {
    try {
      setError(null);
      await messagingService.hideConversation(conversationId);
      setConversations(prev => prev.filter(conv => conv.id !== conversationId));
      
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(null);
        setMessages([]);
      }
    } catch (err) {
      console.error('Failed to hide conversation:', err);
      setError('Failed to hide conversation');
    }
  }, [selectedConversation]);

  // Hide a conversation from user's view
  const hideConversation = useCallback(async (conversationId) => {
    try {
      setError(null);
      await messagingService.hideConversation(conversationId);
      setConversations(prev => prev.filter(conv => conv.id !== conversationId));
      
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(null);
        setMessages([]);
      }
    } catch (err) {
      console.error('Failed to hide conversation:', err);
      setError('Failed to hide conversation');
    }
  }, [selectedConversation]);

  // Update conversation
  const updateConversation = useCallback(async (conversationId, updates) => {
    try {
      setError(null);
      const updatedConversation = await messagingService.updateConversation(conversationId, updates);
      setConversations(prev => prev.map(conv => 
        conv.id === conversationId ? updatedConversation : conv
      ));
      
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(updatedConversation);
      }
      
      return updatedConversation;
    } catch (err) {
      console.error('Failed to update conversation:', err);
      setError('Failed to update conversation');
      return null;
    }
  }, [selectedConversation]);

  // Select conversation
  const selectConversation = useCallback((conversation) => {
    invalidateMessagingCacheIfUserChanged();
    setSelectedConversation(conversation);
    if (!conversation) {
      setMessages([]);
      return;
    }
    const cached = getCachedThread(conversation.id);
    if (cached?.messages?.length) {
      setMessages(decryptMessages(cached.messages));
    } else {
      setMessages([]);
    }
    setUnreadCounts(prev => ({ ...prev, [conversation.id]: 0 }));
    setConversations(prev => prev.map(c => c.id === conversation.id ? { ...c, unread_count: 0 } : c));
    loadMessages(conversation.id);
  }, [loadMessages, invalidateMessagingCacheIfUserChanged, decryptMessages]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);


  // Initial load
  useEffect(() => {
    const user = getCurrentUser();
    setCurrentUser(user);
    loadConversations();
  }, [loadConversations, getCurrentUser]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await ensureMessagingIdentity(messagingService);
      } catch (e) {
        if (!cancelled) {
          console.error('Messaging E2EE key setup failed:', e);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // WebSocket connection for live messages
  useEffect(() => {
    try { wsRef.current && wsRef.current.close(); } catch (_) {}
    if (!selectedConversation?.id) return;
    try {
      const token = safeStorage.getItem('token');
      // Derive WS base from API baseURL to avoid mismatched ports/domains
      const apiBase = api?.defaults?.baseURL || window.location.origin;
      const parsed = new URL(apiBase);
      const wsScheme = parsed.protocol === 'https:' ? 'wss' : 'ws';
      const base = `${wsScheme}://${parsed.host}`;
      const url = `${base}/ws/messaging/${selectedConversation.id}/` + (token ? `?token=${encodeURIComponent(token)}` : '');
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data);
          if (data?.type === 'message' && data.message) {
            if (data.message.e2ee_refetch) {
              loadMessages(selectedConversation.id);
            } else {
              appendLiveMessage(selectedConversation.id, data.message);
            }
          } else if (data?.type === 'reaction') {
            applyReactionUpdate(selectedConversation.id, data);
          }
        } catch (_) {}
      };
      ws.onerror = () => {};
      ws.onclose = () => {};
    } catch (_) {}
    return () => { try { wsRef.current && wsRef.current.close(); } catch (_) {} };
  }, [selectedConversation, appendLiveMessage, applyReactionUpdate, loadMessages]);

  // Cleanup typing timeout
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  return {
    // State
    conversations,
    selectedConversation,
    messages,
    loading,
    sending,
    error,
    typingUsers,
    unreadCounts,
    messagesEndRef,
    currentUser,
    loadingOlder,
    hasNewMessages,
    
    // Actions
    loadConversations,
    loadMessages,
    loadOlder,
    sendMessage,
    createConversation,
    findDirectMessage,
    selectConversation,
    deleteMessage,
    deleteConversation,
    hideConversation,
    updateConversation,
    searchMessages,
    handleTyping,
    clearError,
    // Helpers to manage bottom state/new badge could be added here if needed
    hasMoreBefore,
    
    
    // Computed values
    totalUnreadCount: Object.values(unreadCounts).reduce((sum, count) => sum + count, 0)
  };
};
